// ============================================================
// WINNERSAURUS — useTutorial hook (v2)
//
// Manages tutorial state for first-time paying users.
//
// Trigger condition (all must be true):
//   1. User is logged in (user object provided)
//   2. User has an active subscription (isPremium = true)
//   3. tutorial_completed_at is NULL in their profile
//
// Changes from v1:
//   - Accepts `isPremium` as a parameter instead of querying
//     the subscriptions table separately. This avoids a double
//     query since AuthContext already derives premium status.
//
// ============================================================

import { useState, useEffect, useCallback } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
);

export function useTutorial(user, isPremium) {
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [hasChecked, setHasChecked] = useState(false);

  // ── Check whether to trigger the tutorial on mount ──────────
  useEffect(() => {
    let cancelled = false;

    async function checkTutorialStatus() {
      // Need a logged-in premium user
      if (!user?.id || !isPremium) {
        setHasChecked(true);
        return;
      }

      try {
        const { data: profile, error: profileErr } = await supabase
          .from('profiles')
          .select('tutorial_completed_at')
          .eq('id', user.id)
          .single();

        if (profileErr) {
          console.warn('[useTutorial] could not load profile:', profileErr.message);
          if (!cancelled) setHasChecked(true);
          return;
        }

        // If they've already seen it, never auto-trigger
        if (profile?.tutorial_completed_at) {
          if (!cancelled) setHasChecked(true);
          return;
        }

        // First-time premium user — trigger the tutorial
        if (!cancelled) {
          setIsActive(true);
          setCurrentStep(0);
          setHasChecked(true);
        }
      } catch (e) {
        console.error('[useTutorial] unexpected error:', e);
        if (!cancelled) setHasChecked(true);
      }
    }

    checkTutorialStatus();
    return () => { cancelled = true; };
  }, [user?.id, isPremium]);

  // ── Mark tutorial as done in DB ─────────────────────────────
  const markCompleted = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ tutorial_completed_at: new Date().toISOString() })
        .eq('id', user.id);
      if (error) {
        console.warn('[useTutorial] could not save completion:', error.message);
      }
    } catch (e) {
      console.error('[useTutorial] save error:', e);
    }
  }, [user?.id]);

  // ── Advance to next step ────────────────────────────────────
  const nextStep = useCallback((totalSteps) => {
    setCurrentStep(prev => {
      if (prev + 1 >= totalSteps) {
        markCompleted();
        setIsActive(false);
        return 0;
      }
      return prev + 1;
    });
  }, [markCompleted]);

  // ── Skip the entire tutorial ────────────────────────────────
  const skipTutorial = useCallback(() => {
    markCompleted();
    setIsActive(false);
    setCurrentStep(0);
  }, [markCompleted]);

  // ── Complete the tutorial (after final step) ────────────────
  const completeTutorial = useCallback(() => {
    markCompleted();
    setIsActive(false);
    setCurrentStep(0);
  }, [markCompleted]);

  // ── Restart tutorial manually (from profile menu) ───────────
  // Does NOT touch the DB — just shows the overlay again
  const restartTutorial = useCallback(() => {
    setIsActive(true);
    setCurrentStep(0);
  }, []);

  return {
    isActive,
    currentStep,
    hasChecked,
    nextStep,
    skipTutorial,
    completeTutorial,
    restartTutorial,
  };
}
